import { chain } from "ramda";
import { blobToBase64Async } from "../utility/attachment.encode";
import { showMessageBox } from "./UI/messageBox";
import promptBox, { PROMPTBOX_RESULT_YES, PROMPTBOX_TYPE_YESNO } from "./UI/promptBox";

function fileDropHandler(dataSources, sampleDataSource, renderView) {

    // MultiDropDefinition is { dsName: String, repeaterPath: String, attachmentPath: String, xpathsrc: String }
    // MultiDropMatch is { definition: MultiDropDefinition, repeater: DataSourceNode, targetNode: DataSourceNode }

    let multiDropTargets = [];

    // MultiDropDefinition -> ()
    function registerMultiDrop(definition) {
        multiDropTargets.push(definition);
    }

    // (MultiDropDefinition, DataSourceNode) -> [MultiDropMatch]
    function findMultiDropMatches(targetDef, node) {
        var ds = dataSources.getDom(targetDef.dsName);

        return ds.selectNodes(targetDef.repeaterPath)
            .filter(function (repeater) {
                return repeater.selectNodes(targetDef.attachmentPath).some(function (att) {
                    return att.getNode() === node.getNode();
                });
            })
            .map(function (repeater) {
                return {
                    definition: targetDef,
                    repeater: repeater,
                    targetNode: node
                };
            });
    }

    // DataSourceNode -> MultiDropMatch
    function findMultiDropMatch(node) {
        return chain(function (target) {
            return findMultiDropMatches(target, node);
        }, multiDropTargets)[0];
    }

    // EventProps -> DataSourceNode
    function getTargetNode(props) {
        return dataSources.getNodeById(props.nodeId);
    }

    // (DataSourceNode, String, FileDropData) -> Promise
    function setFileAsync(targetNode, encodingType, file) {
        return blobToBase64Async(file, encodingType)
            .then(function (base64) {
                return targetNode.setValueAsync(base64);
            });
    }

    //  () -> Promise[Boolean]
    async function showReplacePromptAsync() {
        const box = promptBox({
            title: 'Replace file?',
            text: 'This control already has a file in it. Do you want to replace it?',
            type: PROMPTBOX_TYPE_YESNO
        });

        const result = await box.showAsync();
        
        return result === PROMPTBOX_RESULT_YES;
    }

    // MultiDropDefinition -> DomNode
    async function getMultiDropBaseNodeAsync(definition) {
        const xpath = definition.xpathsrc || definition.repeaterPath;

        const sampleData = await sampleDataSource.initAsync();

        return sampleData.selectSampleNode(definition.dsName, xpath);
    }

    // (String, DataSourceNode) -> DataSourceNode
    function firstAttachmentNode(attachmentPath, node) {
        var att = node.selectSingle(attachmentPath);

        if (!att) {
            throw new Error('Unable to find field at ' + attachmentPath + ' under inserted node');
        }

        return att;
    }

    // (DataSourceNode, DomNode, String, Number) -> ()
    async function insertExtraNodesAsync(insertionPoint, baseNode, attachmentPath, count) {
        // Create iterable array of length <count>
        const a = Array.apply(null, Array(count));
        let insertedNodes = [];
        let last = null;

        for (const _ of a) {
            const inserted = await (last || insertionPoint)
                .insertAfterAsync(baseNode.cloneNode(true));

            insertedNodes.push(inserted);

            last = inserted;
        }

        return insertedNodes.map((n) =>
            firstAttachmentNode(attachmentPath, n)
        );
    }

    // (String, [DataSourceNode], [File]) -> Promise
    async function setFilesAsync(encodingType, fileNodes, files) {
        var i = -1;

        for (const node of fileNodes) {
            i += 1;

            await setFileAsync(node, encodingType, files[i]);
        }
    }

    // (MultiDropMatch, FileDropEvent) -> Promise
    function insertToMultiDropAsync(multiDropMatch, event) {
        var files = event.data.files;

        return getMultiDropBaseNodeAsync(multiDropMatch.definition)
            .then(function (baseNode) {
                if (!baseNode) {
                    throw new Error(
                        'Unable to determine base node for inserting multiple files.\nYou may need to use the /xpathsrc option when registering multi-drop support.');
                }

                return insertExtraNodesAsync(
                    multiDropMatch.repeater,
                    baseNode,
                    multiDropMatch.definition.attachmentPath,
                    files.length - 1
                );
            })
            .then(function (insertedTargetFiles) {
                var targetNodes = [multiDropMatch.targetNode].concat(insertedTargetFiles);

                return setFilesAsync(event.targetType, targetNodes, files);
            });
    }

    // DataSourceNode -> Promise[Boolean]
    function checkForReplace(field, action) {
        return field.value()
            ? showReplacePromptAsync().then(function (r) { if (r) { return action(); } })
            : action();
    }

    // (FileDropEvent, DataSourceNode) -> Promise
    function handleMultiDropAsync(event, targetNode) {
        var multiDropMatch = findMultiDropMatch(targetNode);

        if (!multiDropMatch) {
            return showMessageBox('Multiple files not allowed', 'You cannot drop multiple files here.');
        }

        return checkForReplace(targetNode, function () {
            return insertToMultiDropAsync(multiDropMatch, event);
        });
    }

    // (FileDropEvent, DataSourceNode) -> Promise
    function handleSingleDropAsync(event, targetNode) {
        var file = event.data.files[0];
        var encodingType = event.targetType;

        return checkForReplace(targetNode, function () {
            return setFileAsync(targetNode, encodingType, file);
        });
    }

    // FileDropEvent -> Promise
    async function tryUpdateFileAsync(event) {
        const fileCount = event.data.files.length;

        // Probably should not happen but just in case
        if (fileCount === 0) {
            console.warn('File drop event with no files');

            return;
        }

        const targetNode = getTargetNode(event.props);

        if (fileCount > 1) {
            await handleMultiDropAsync(event, targetNode);
        } else {
            await handleSingleDropAsync(event, targetNode);
        }
    }

    // FileDropEvent -> Promise
    async function fileDropAsync(event) {
        try {
            try {
                await tryUpdateFileAsync(event);
            } finally {
                event.data.clear();
            }
        } catch (e) {
            console.error(e);

            await showMessageBox('Error', `An error occurred attempting to add the dropped file(s):\n\n${e.message}`);
        }

        await renderView();
    }

    return {
        fileDropAsync,
        registerMultiDrop,
    };
}

export default fileDropHandler;